import React, { useEffect, useMemo, useRef, useState } from "react";
import { mergeCustomerOptionLists, customerNameExists } from "./addProjectApi";

const CUSTOMER_STORAGE_KEY = "pb.plugin.addProjectCustomers.v1";

function readStoredCustomCustomers() {
  try {
    const raw = localStorage.getItem(CUSTOMER_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed.map((x) => String(x || "").trim()).filter(Boolean))];
  } catch {
    return [];
  }
}

function writeStoredCustomCustomers(names) {
  try {
    localStorage.setItem(CUSTOMER_STORAGE_KEY, JSON.stringify(names));
  } catch {
    /* ignore */
  }
}

export default function CustomerPicker({ value, onChange, options, disabled, required }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [customCustomers, setCustomCustomers] = useState(() => readStoredCustomCustomers());

  const allOptions = useMemo(
    () => mergeCustomerOptionLists(options, customCustomers),
    [options, customCustomers]
  );

  const trimmed = String(value || "").trim();
  const filtered = useMemo(() => {
    if (!trimmed) return allOptions.slice(0, 40);
    const q = trimmed.toLowerCase();
    return allOptions.filter((name) => name.toLowerCase().includes(q)).slice(0, 40);
  }, [allOptions, trimmed]);

  const canAddNew = trimmed.length > 0 && !customerNameExists(trimmed, allOptions);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const addCustomer = (name) => {
    const nextName = String(name || "").trim();
    if (!nextName) return;
    if (!customerNameExists(nextName, customCustomers)) {
      const next = mergeCustomerOptionLists(customCustomers, [nextName]);
      setCustomCustomers(next);
      writeStoredCustomCustomers(next);
    }
    onChange(nextName);
    setOpen(false);
  };

  return (
    <div className="add-project-customer-picker" ref={rootRef}>
      <input
        type="text"
        className="add-project-customer-input"
        value={value}
        required={required}
        disabled={disabled}
        placeholder="Search or type customer name"
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && !disabled ? (
        <div className="add-project-customer-menu" role="listbox">
          {filtered.length ? (
            filtered.map((name) => (
              <button
                key={name}
                type="button"
                className="add-project-customer-option"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addCustomer(name)}
              >
                {name}
              </button>
            ))
          ) : (
            <p className="add-project-customer-empty">No matching customers.</p>
          )}
          {canAddNew ? (
            <button
              type="button"
              className="add-project-customer-add"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addCustomer(trimmed)}
            >
              Add &ldquo;{trimmed}&rdquo; as new customer
            </button>
          ) : null}
        </div>
      ) : null}
      {trimmed && canAddNew ? (
        <button
          type="button"
          className="add-project-customer-add-inline"
          disabled={disabled}
          onClick={() => addCustomer(trimmed)}
        >
          Add &ldquo;{trimmed}&rdquo; as new customer
        </button>
      ) : null}
    </div>
  );
}
